import React, { ReactNode, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Button } from "@cofano/cck";
import { intl } from "LocaleWrapper";

export type BaseDialogProps = {
	open: boolean;
	onClose: () => void;
	title?: ReactNode;
	maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
	children: ReactNode;
	dialogStyling?: {
		content?: React.CSSProperties;
		actions?: React.CSSProperties;
	};
};

export type CustomDialogProps = BaseDialogProps & {
	actions?: ReactNode;
};

export type FormDialogProps = BaseDialogProps & {
	form: string;
	loading?: boolean;
	confirmText?: ReactNode;
	saveButtonDisabled?: boolean;
};

export const CustomDialog = ({ open, onClose, title, maxWidth, actions, children, dialogStyling }: CustomDialogProps) => {
	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (reason !== "backdropClick") {
					onClose();
				}
			}}
			scroll='paper'
			maxWidth={maxWidth}
			fullWidth>
			<DialogTitle style={{ color: "inherit" }}>
				{title}
			</DialogTitle>

			<DialogContent style={dialogStyling?.content} dividers>
				{children}
			</DialogContent>

			<DialogActions style={dialogStyling?.actions}>
				<Button
					onClick={onClose}
					size="small"
					variant="text">
					{intl.formatMessage({ id: "CLOSE" })}
				</Button>
				{actions}
			</DialogActions>
		</Dialog>
	);
};

export const FormDialog = ({
	open,
	onClose,
	title,
	form,
	maxWidth,
	loading,
	children,
	dialogStyling,
	confirmText,
	saveButtonDisabled,
}: FormDialogProps) => {
	return (
		<Dialog
			open={open}
			onClose={(_, reason) => {
				if (reason !== "backdropClick") {
					onClose();
				}
			}}
			scroll='paper'
			maxWidth={maxWidth}
			fullWidth>
			<DialogTitle style={{ color: "inherit" }}>
				{title}
			</DialogTitle>

			<DialogContent dividers style={dialogStyling?.content}>
				{children}
			</DialogContent>

			<DialogActions style={dialogStyling?.actions}>
				<Button
					onClick={onClose}
					disabled={loading}
					size="small"
					variant="text">
					{intl.formatMessage({ id: "CANCEL" })}
				</Button>

				<Button
					type="submit"
					form={form}
					loading={loading}
					disabled={loading || saveButtonDisabled}
					size="small">
					{confirmText ?? intl.formatMessage({ id: "SAVE" })}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export type FormDialogComponentProps<T,> = {
	open: boolean;
	onClose: () => void;
	entity?: T | null;
	onSave: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useFormDialog = <T,>({ onSave }: {  onSave: () => void; }) => {

	const [ state, setState ] = useState<{ open: boolean, entity?: T }>({
		open: false,
		entity: undefined,
	});

	return {
		formDialogProps: {
			open: state.open,
			onClose: () => {
				setState({
					open: false,
					entity: undefined,
				});
			},
			entity: state.entity,
			onSave: onSave,
		},

		openFormDialog: (entity?: T) => {
			setState({
				open: true,
				entity: entity,
			});
		},
	};
};
